<p-toast />
<p-confirmDialog />

<div class="flex flex-col gap-6 p-6">
    <!-- Page Header -->
    <div class="flex items-center justify-between">
        <h2 class="text-2xl font-bold text-surface-900">
            @if (vm.activeTab() === 'generate') {
                Generate Report
            } @else {
                Report
            }
        </h2>
        <div class="flex items-center gap-2">
            @if (vm.activeTab() === 'list') {
                <p-button
                    label="Generate Report"
                    icon="pi pi-plus"
                    (onClick)="navigateToTab('generate')" />
            } @else {
                <p-button
                    label="Back to Report List"
                    icon="pi pi-arrow-left"
                    severity="secondary"
                    [outlined]="true"
                    (onClick)="navigateToTab('list')" />
            }
        </div>
    </div>

    <!-- ==================== GENERATE REPORT TAB ==================== -->
    @if (vm.activeTab() === 'generate') {

    <!-- Accordion Panel 1: Report Type & Scope -->
    <div class="accordion-panel"
        [class.panel-active]="vm.activeStep() === 1"
        [class.panel-completed]="vm.activeStep() > 1">
        <!-- Panel 1 Header -->
        <div class="accordion-header"
            [class.header-active]="vm.activeStep() === 1"
            (click)="vm.goToStep(1)">
            <div class="flex items-center gap-3">
                <span class="step-number"
                    [class.step-active]="vm.activeStep() === 1"
                    [class.step-completed]="vm.activeStep() > 1">
                    @if (vm.activeStep() > 1) {
                        <i class="pi pi-check text-sm"></i>
                    } @else {
                        1
                    }
                </span>
                <div>
                    <div class="text-base font-bold text-surface-800">Report Type & Scope</div>
                    @if (vm.activeStep() > 1) {
                        <div class="text-xs font-semibold" style="color: #059669;">
                            {{ vm.getReportTypeLabel() }} &bull; {{ vm.getSelectedClientLabel() }} @if (vm.selectedExecutionRunId()) { &bull; {{ vm.getSelectedRunLabel() }} }
                        </div>
                    } @else {
                        <div class="text-xs text-surface-500">Choose report type and configure data scope</div>
                    }
                </div>
            </div>
            <div class="flex items-center gap-3">
                @if (vm.activeStep() > 1) {
                    <div class="flex flex-wrap gap-1.5">
                        <span class="summary-chip type-chip">{{ vm.getReportTypeLabel() }}</span>
                        @if (vm.getSelectedClientLabel()) {
                            <span class="summary-chip">{{ vm.getSelectedClientLabel() }}</span>
                        }
                        @if (vm.selectedExecutionRunId()) {
                            <span class="summary-chip">{{ vm.getSelectedRunLabel() }}</span>
                        }
                    </div>
                    <span class="badge-completed">
                        <i class="pi pi-check text-xs"></i> Done
                    </span>
                }
                @if (vm.activeStep() === 1) {
                    <span class="badge-active">In Progress</span>
                }
                <i class="pi text-surface-400 transition-transform"
                    [class.pi-chevron-up]="vm.activeStep() === 1"
                    [class.pi-chevron-down]="vm.activeStep() !== 1"></i>
            </div>
        </div>
        <!-- Panel 1 Body -->
        @if (vm.activeStep() === 1) {
            <div class="accordion-body">
                <!-- Report Type Selection -->
                <div class="flex items-center gap-2 mb-4">
                    <span class="text-xs font-bold text-surface-600 uppercase tracking-wider">Select Report Type</span>
                    <div class="flex-1 h-px bg-surface-200"></div>
                </div>
                <div class="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
                    @for (opt of vm.reportTypeOptions; track opt.id) {
                        <div
                            class="cursor-pointer rounded-lg border-2 p-4 transition-all hover:shadow-md"
                            [class.border-primary]="vm.selectedReportType() === opt.id"
                            [class.bg-primary-50]="vm.selectedReportType() === opt.id"
                            [class.border-surface-200]="vm.selectedReportType() !== opt.id"
                            (click)="vm.onReportTypeChange(opt.id)">
                            <div class="flex flex-col items-center text-center gap-2">
                                <i [class]="opt.icon + ' text-2xl'" [class.text-primary]="vm.selectedReportType() === opt.id" [class.text-surface-400]="vm.selectedReportType() !== opt.id"></i>
                                <span class="font-semibold text-sm text-surface-800">{{ opt.label }}</span>
                                <span class="text-xs text-surface-500 leading-tight">{{ opt.description }}</span>
                            </div>
                        </div>
                    }
                </div>

                <!-- Scope Configuration (shown after type selection) -->
                @if (vm.selectedReportType()) {
                    <div class="border-t border-surface-200 pt-6">
                        <div class="flex items-center gap-2 mb-4">
                            <span class="text-xs font-bold text-surface-600 uppercase tracking-wider">Configure Scope</span>
                            <div class="flex-1 h-px bg-surface-200"></div>
                        </div>
                        <p-fluid>
                            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">

                                <!-- Client -->
                                <div class="flex flex-col gap-2">
                                    <label class="text-sm font-semibold text-surface-700">Client <span class="text-red-500">*</span></label>
                                    <p-select
                                        [options]="vm.clientOptions()"
                                        [ngModel]="vm.selectedClient()"
                                        (onChange)="vm.onClientChange($event.value)"
                                        optionLabel="label"
                                        optionValue="value"
                                        placeholder="Select client"
                                        [disabled]="vm.isClientDropdownDisabled()"
                                        [appendTo]="'body'">
                                    </p-select>
                                </div>

                                <!-- Select Type (shown for comparison mode, after client) -->
                                @if (vm.isComparisonMode()) {
                                    <div class="flex flex-col gap-2">
                                        <label class="text-sm font-semibold text-surface-700">Select Type <span class="text-red-500">*</span></label>
                                        <div class="flex items-center justify-start h-[42px]">
                                            @for (opt of vm.comparisonSelectTypeOptions; track opt.value) {
                                                <div class="flex items-center gap-2 w-1/2">
                                                    <p-radioButton
                                                        [name]="'comparisonSelectType'"
                                                        [value]="opt.value"
                                                        [ngModel]="vm.selectedComparisonSelectType()"
                                                        (ngModelChange)="vm.onComparisonSelectTypeChange($event)">
                                                    </p-radioButton>
                                                    <label class="text-sm text-surface-700 cursor-pointer">{{ opt.label }}</label>
                                                </div>
                                            }
                                        </div>
                                    </div>
                                }

                                <!-- Execution Run (shown for execution-summary OR comparison with execution-run type) -->
                                @if (vm.needsExecutionRun() && !vm.isTenantComparisonMode()) {
                                    <div class="flex flex-col gap-2">
                                        <label class="text-sm font-semibold text-surface-700">Baseline Execution Run <span class="text-red-500">*</span></label>
                                        <p-select
                                            [options]="vm.filteredExecutionRuns()"
                                            [ngModel]="vm.selectedExecutionRunId()"
                                            (ngModelChange)="vm.selectedExecutionRunId.set($event)"
                                            optionLabel="runId"
                                            optionValue="id"
                                            placeholder="Select execution run"
                                            [filter]="true"
                                            filterBy="runId,packageName,executedByName">
                                            <ng-template #selectedItem let-item>
                                                @if (item) {
                                                    <div class="flex items-center gap-2">
                                                        <span class="font-mono text-xs font-semibold text-primary">{{ item.runId }}</span>
                                                        <span class="text-surface-400">|</span>
                                                        <span class="text-xs text-surface-600">{{ item.packageName }}</span>
                                                        <span class="text-surface-400">|</span>
                                                        <span class="text-xs font-semibold" [style.color]="vm.getPassRateColor(item.passRate)">{{ item.passRate }}%</span>
                                                    </div>
                                                }
                                            </ng-template>
                                            <ng-template #item let-run>
                                                <div class="flex flex-col gap-1 py-1">
                                                    <div class="flex items-center gap-2">
                                                        <span class="font-mono text-xs font-bold text-primary">{{ run.runId }}</span>
                                                        <span class="text-surface-300">|</span>
                                                        <span class="text-xs text-surface-500">{{ run.executedOn | date:'MMM dd, yyyy hh:mm a' }}</span>
                                                        <span class="text-surface-300">|</span>
                                                        <span class="text-xs text-surface-600">{{ run.packageName }}</span>
                                                    </div>
                                                    <div class="flex items-center gap-3 text-xs text-surface-400">
                                                        <span>{{ run.scenarioCount }} scenarios</span>
                                                        <span class="font-semibold" [style.color]="vm.getPassRateColor(run.passRate)">{{ run.passRate }}% passed</span>
                                                        <span>{{ run.executedByName }}</span>
                                                    </div>
                                                </div>
                                            </ng-template>
                                        </p-select>
                                    </div>
                                }

                                <!-- Comparison Run (execution-run mode) -->
                                @if (vm.isExecutionRunComparisonMode()) {
                                    <div class="flex flex-col gap-2">
                                        <label class="text-sm font-semibold text-surface-700">Compare With Execution Run <span class="text-red-500">*</span></label>
                                        <p-select
                                            [options]="vm.filteredComparisonRuns()"
                                            [ngModel]="vm.selectedComparisonRunId()"
                                            (ngModelChange)="vm.selectedComparisonRunId.set($event)"
                                            optionLabel="runId"
                                            optionValue="id"
                                            placeholder="Select comparison run"
                                            [filter]="true"
                                            filterBy="runId,packageName,executedByName">
                                            <ng-template #selectedItem let-item>
                                                @if (item) {
                                                    <div class="flex items-center gap-2">
                                                        <span class="font-mono text-xs font-semibold text-primary">{{ item.runId }}</span>
                                                        <span class="text-surface-400">|</span>
                                                        <span class="text-xs text-surface-600">{{ item.packageName }}</span>
                                                        <span class="text-surface-400">|</span>
                                                        <span class="text-xs font-semibold" [style.color]="vm.getPassRateColor(item.passRate)">{{ item.passRate }}%</span>
                                                    </div>
                                                }
                                            </ng-template>
                                            <ng-template #item let-run>
                                                <div class="flex flex-col gap-1 py-1">
                                                    <div class="flex items-center gap-2">
                                                        <span class="font-mono text-xs font-bold text-primary">{{ run.runId }}</span>
                                                        <span class="text-surface-300">|</span>
                                                        <span class="text-xs text-surface-500">{{ run.executedOn | date:'MMM dd, yyyy hh:mm a' }}</span>
                                                        <span class="text-surface-300">|</span>
                                                        <span class="text-xs text-surface-600">{{ run.packageName }}</span>
                                                    </div>
                                                    <div class="flex items-center gap-3 text-xs text-surface-400">
                                                        <span>{{ run.scenarioCount }} scenarios</span>
                                                        <span class="font-semibold" [style.color]="vm.getPassRateColor(run.passRate)">{{ run.passRate }}% passed</span>
                                                        <span>{{ run.executedByName }}</span>
                                                    </div>
                                                </div>
                                            </ng-template>
                                        </p-select>
                                    </div>
                                }

                                <!-- Tenant Type Comparison (baseline tenant) -->
                                @if (vm.isTenantComparisonMode()) {
                                    <div class="flex flex-col gap-2">
                                        <label class="text-sm font-semibold text-surface-700">Baseline Tenant <span class="text-red-500">*</span></label>
                                        <p-select
                                            [options]="vm.tenantOptions()"
                                            [ngModel]="vm.selectedBaselineTenantId()"
                                            (ngModelChange)="vm.selectedBaselineTenantId.set($event)"
                                            optionLabel="label"
                                            optionValue="value"
                                            placeholder="Select baseline tenant"
                                            [filter]="true"
                                            filterBy="label"
                                            [appendTo]="'body'">
                                        </p-select>
                                    </div>
                                    <div class="flex flex-col gap-2">
                                        <label class="text-sm font-semibold text-surface-700">Compare With Tenant <span class="text-red-500">*</span></label>
                                        <p-select
                                            [options]="vm.filteredComparisonTenants()"
                                            [ngModel]="vm.selectedComparisonTenantId()"
                                            (ngModelChange)="vm.selectedComparisonTenantId.set($event)"
                                            optionLabel="label"
                                            optionValue="value"
                                            placeholder="Select comparison tenant"
                                            [filter]="true"
                                            filterBy="label"
                                            [appendTo]="'body'">
                                        </p-select>
                                    </div>
                                }

                                <!-- Package -->
                                @if (vm.selectedReportType() !== 'execution-summary') {
                                    <div class="flex flex-col gap-2">
                                        <label class="text-sm font-semibold text-surface-700">Package</label>
                                        <p-select
                                            [options]="vm.packageOptions()"
                                            [ngModel]="vm.selectedPackage()"
                                            (ngModelChange)="vm.selectedPackage.set($event); vm.onPackageChange()"
                                            (onClear)="vm.selectedPackage.set(null); vm.onPackageChange()"
                                            optionLabel="label"
                                            optionValue="value"
                                            placeholder="Packages"
                                            [showClear]="true">
                                        </p-select>
                                    </div>

                                    <!-- Test Type -->
                                    <div class="flex flex-col gap-2">
                                        <label class="text-sm font-semibold text-surface-700">Test Type</label>
                                        <p-select
                                            [options]="vm.testTypeOptions()"
                                            [ngModel]="vm.selectedTestType()"
                                            (ngModelChange)="vm.selectedTestType.set($event); vm.onTestTypeChange()"
                                            (onClear)="vm.selectedTestType.set(null); vm.onTestTypeChange()"
                                            optionLabel="label"
                                            optionValue="value"
                                            placeholder="Test Types"
                                            [showClear]="true">
                                        </p-select>
                                    </div>

                                    <!-- Functional Areas -->
                                    <div class="flex flex-col gap-2">
                                        <label class="text-sm font-semibold text-surface-700">Functional Areas</label>
                                        <p-multiselect
                                            [options]="vm.functionalAreaOptions()"
                                            [ngModel]="vm.selectedFunctionalAreas()"
                                            (ngModelChange)="vm.selectedFunctionalAreas.set($event)"
                                            optionLabel="label"
                                            optionValue="value"
                                            placeholder="Functional Areas"
                                            [maxSelectedLabels]="2"
                                            [filter]="true">
                                        </p-multiselect>
                                    </div>
                                }

                                <!-- Status Filter -->
                                <div class="flex flex-col gap-2">
                                    <label class="text-sm font-semibold text-surface-700">Status</label>
                                    <p-select
                                        [options]="vm.statusFilterOptions"
                                        [ngModel]="vm.selectedStatus()"
                                        (ngModelChange)="vm.selectedStatus.set($event)"
                                        optionLabel="label"
                                        optionValue="value"
                                        placeholder="Select status"
                                        [showClear]="true">
                                    </p-select>
                                </div>

                                <!-- Date Range -->
                                @if (vm.needsDateRange()) {
                                    <div class="flex flex-col gap-2">
                                        <label class="text-sm font-semibold text-surface-700">Date Range</label>
                                        <p-select
                                            [options]="vm.dateRangeModeOptions"
                                            [ngModel]="vm.selectedDateRangeMode()"
                                            (ngModelChange)="vm.onDateRangeModeChange($event)"
                                            optionLabel="label"
                                            optionValue="value"
                                            placeholder="Select date range mode">
                                        </p-select>
                                    </div>
                                    @if (vm.selectedDateRangeMode() === 'fixed') {
                                        <div class="flex flex-col gap-2">
                                            <label class="text-sm font-semibold text-surface-700">Custom Date Range</label>
                                            <p-datepicker
                                                [ngModel]="vm.dateRange()"
                                                (ngModelChange)="vm.dateRange.set($event)"
                                                selectionMode="range"
                                                [maxDate]="vm.maxDate"
                                                placeholder="Select date range"
                                                dateFormat="M dd, yy"
                                                [showIcon]="true">
                                            </p-datepicker>
                                        </div>
                                    }
                                }
                            </div>
                        </p-fluid>
                    </div>
                }

                <!-- Panel Footer -->
                <div class="flex justify-end mt-6 pt-4 border-t border-surface-100">
                    <p-button label="Continue" icon="pi pi-arrow-right" iconPos="right" (onClick)="vm.nextStep()" [disabled]="!vm.isPanel1Valid()" />
                </div>
            </div>
        }
    </div>

    <!-- Accordion Panel 2: Report Options & Export -->
    <div class="accordion-panel"
        [class.panel-active]="vm.activeStep() === 2 && !vm.panel2Completed()"
        [class.panel-completed]="vm.panel2Completed()">
        <!-- Panel 2 Header -->
        <div class="accordion-header"
            [class.header-active]="vm.activeStep() === 2"
            (click)="vm.goToStep(2)">
            <div class="flex items-center gap-3">
                <span class="step-number"
                    [class.step-active]="vm.activeStep() === 2 && !vm.panel2Completed()"
                    [class.step-completed]="vm.panel2Completed()">
                    @if (vm.panel2Completed()) {
                        <i class="pi pi-check text-sm"></i>
                    } @else {
                        2
                    }
                </span>
                <div>
                    <div class="text-base font-bold text-surface-800">Report Options & Export</div>
                    @if (vm.panel2Completed()) {
                        <div class="text-xs font-semibold" style="color: #059669;">
                            {{ vm.getDetailLevelLabel() }} &bull; {{ vm.getExportFormatLabel() }}
                        </div>
                    } @else {
                        <div class="text-xs text-surface-500">Detail level, format, and additional options</div>
                    }
                </div>
            </div>
            <div class="flex items-center gap-3">
                @if (vm.panel2Completed()) {
                    <div class="flex flex-wrap gap-1.5">
                        <span class="summary-chip type-chip">{{ vm.getDetailLevelLabel() }}</span>
                        <span class="summary-chip">{{ vm.getExportFormatLabel() }}</span>
                        @if (vm.includeScreenshots()) {
                            <span class="summary-chip">Screenshots</span>
                        }
                        @if (vm.includeErrorLogs()) {
                            <span class="summary-chip">Error Logs</span>
                        }
                    </div>
                    <span class="badge-completed">
                        <i class="pi pi-check text-xs"></i> Done
                    </span>
                } @else if (vm.activeStep() === 2) {
                    <span class="badge-active">In Progress</span>
                }
                <i class="pi text-surface-400 transition-transform"
                    [class.pi-chevron-up]="vm.activeStep() === 2"
                    [class.pi-chevron-down]="vm.activeStep() !== 2"></i>
            </div>
        </div>
        <!-- Panel 2 Body -->
        @if (vm.activeStep() === 2) {
            <div class="accordion-body">
                <!-- Detail Level (radio group) -->
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div class="flex flex-col gap-2">
                        <label class="text-sm font-semibold text-surface-700">Detail Level <span class="text-red-500">*</span></label>
                        <div class="flex flex-wrap gap-2">
                            @for (opt of vm.detailLevelOptions; track opt.value) {
                                <span
                                    class="radio-chip"
                                    [class.selected]="vm.selectedDetailLevel() === opt.value"
                                    (click)="vm.selectedDetailLevel.set(opt.value)">
                                    {{ opt.label }}
                                </span>
                            }
                        </div>
                    </div>

                    <!-- Export Format (radio group) -->
                    <div class="flex flex-col gap-2">
                        <label class="text-sm font-semibold text-surface-700">Export Format <span class="text-red-500">*</span></label>
                        <div class="flex flex-wrap gap-2">
                            @for (opt of vm.exportFormatOptions; track opt.value) {
                                <span
                                    class="radio-chip"
                                    [class.selected]="vm.selectedExportFormat() === opt.value"
                                    (click)="vm.selectedExportFormat.set(opt.value)">
                                    {{ opt.label }}
                                </span>
                            }
                        </div>
                    </div>
                </div>

                <!-- Inclusions Section -->
                <div class="flex items-center gap-2 mb-4">
                    <span class="text-xs font-bold text-surface-600 uppercase tracking-wider">Inclusions</span>
                    <div class="flex-1 h-px bg-surface-200"></div>
                </div>
                <div class="max-w-lg">
                    <div class="flex items-center justify-between py-3 border-b border-surface-100">
                        <div>
                            <div class="text-sm text-surface-700">Include Screenshots</div>
                            <div class="text-xs text-surface-400">Attach step screenshots to the report</div>
                        </div>
                        <p-toggleswitch
                            [ngModel]="vm.includeScreenshots()"
                            (ngModelChange)="vm.includeScreenshots.set($event)">
                        </p-toggleswitch>
                    </div>
                    <div class="flex items-center justify-between py-3 border-b border-surface-100">
                        <div>
                            <div class="text-sm text-surface-700">Include Error Logs</div>
                            <div class="text-xs text-surface-400">Show error details for failed steps</div>
                        </div>
                        <p-toggleswitch
                            [ngModel]="vm.includeErrorLogs()"
                            (ngModelChange)="vm.includeErrorLogs.set($event)">
                        </p-toggleswitch>
                    </div>
                </div>

                <!-- Action Buttons -->
                <div class="flex items-center justify-between mt-6 pt-4 border-t border-surface-100">
                    <p-button label="Back" icon="pi pi-arrow-left" severity="secondary" [outlined]="true" (onClick)="vm.prevStep()" />
                    <div class="flex items-center gap-3">
                        <p-button
                            label="Generate Preview"
                            icon="pi pi-eye"
                            [outlined]="true"
                            (onClick)="vm.generatePreview()"
                            [disabled]="!vm.canGenerate()">
                        </p-button>
                        <p-button
                            label="Export Report"
                            icon="pi pi-download"
                            severity="success"
                            (onClick)="vm.exportReport()"
                            [disabled]="vm.previewSummary().totalScenarios === 0 || vm.exportLoading()"
                            [loading]="vm.exportLoading()">
                        </p-button>
                        <p-button
                            [label]="vm.isEditMode() ? 'Update Report' : 'Save Report'"
                            [icon]="vm.isEditMode() ? 'pi pi-check' : 'pi pi-save'"
                            severity="info"
                            (onClick)="vm.openSaveDialog()"
                            [disabled]="!vm.showPreview() || (vm.isEditMode() && !vm.hasEditChanges())">
                        </p-button>
                    </div>
                </div>
            </div>
        }
    </div>

        <!-- Loading Spinner -->
        @if (vm.isGenerating()) {
            <div class="flex items-center justify-center py-4">
                <p-progressSpinner strokeWidth="4" animationDuration="0.8s" [style]="{width: '28px', height: '28px'}" />
                <span class="ml-2 text-surface-500 text-sm">Generating report preview...</span>
            </div>
        }

        <!-- Preview Section: Standard Reports -->
        @if (vm.showPreview() && !vm.isGenerating()) {
            <div class="bg-surface-0 rounded-xl border border-surface-200 p-6">
                <div class="flex items-center justify-between mb-5">
                    <h3 class="text-lg font-semibold text-surface-800">
                        <i class="pi pi-file-check mr-2 text-primary"></i>
                        {{ vm.getReportTitle() }} — Preview
                    </h3>
                    <span class="text-xs text-surface-400">Generated: {{ today | date:'medium' }}</span>
                </div>

                <!-- KPI Cards: Custom / Execution Summary -->
                @if (vm.selectedReportType() !== 'trend' && vm.selectedReportType() !== 'failure-analysis') {
                    <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
                        <div class="rounded-lg border border-surface-200 bg-blue-50 p-4 text-center">
                            <div class="text-2xl font-extrabold text-blue-700">{{ vm.previewSummary().totalScenarios }}</div>
                            <div class="text-xs font-semibold text-surface-500 uppercase tracking-wide mt-1">Total Scenarios</div>
                        </div>
                        <div class="rounded-lg border border-green-200 bg-green-50 p-4 text-center">
                            <div class="text-2xl font-extrabold text-green-700">{{ vm.previewSummary().totalPassed }}</div>
                            <div class="text-xs font-semibold text-surface-500 uppercase tracking-wide mt-1">Passed</div>
                        </div>
                        <div class="rounded-lg border border-red-200 bg-red-50 p-4 text-center">
                            <div class="text-2xl font-extrabold text-red-700">{{ vm.previewSummary().totalFailed }}</div>
                            <div class="text-xs font-semibold text-surface-500 uppercase tracking-wide mt-1">Failed</div>
                        </div>
                        <div class="rounded-lg border border-surface-200 bg-surface-50 p-4 text-center">
                            <div class="text-2xl font-extrabold text-surface-600">{{ vm.previewSummary().totalStopped }}</div>
                            <div class="text-xs font-semibold text-surface-500 uppercase tracking-wide mt-1">Stopped</div>
                        </div>
                        <div class="rounded-lg border border-green-300 bg-green-50 p-4 text-center">
                            <div class="text-2xl font-extrabold" [style.color]="vm.getPassRateColor(vm.previewSummary().passRate)">{{ vm.previewSummary().passRate }}%</div>
                            <div class="text-xs font-semibold text-surface-500 uppercase tracking-wide mt-1">Pass Rate</div>
                        </div>
                    </div>
                }

                <!-- KPI Cards: Trend Report -->
                @if (vm.selectedReportType() === 'trend') {
                    <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
                        <div class="rounded-lg border border-surface-200 bg-blue-50 p-4 text-center">
                            <div class="text-2xl font-extrabold text-blue-700">{{ vm.trendSummary().runsAnalyzed }}</div>
                            <div class="text-xs font-semibold text-surface-500 uppercase tracking-wide mt-1">Runs Analyzed</div>
                        </div>
                        <div class="rounded-lg border border-green-200 bg-green-50 p-4 text-center">
                            <div class="text-2xl font-extrabold text-green-700">{{ vm.trendSummary().avgPassRate }}%</div>
                            <div class="text-xs font-semibold text-surface-500 uppercase tracking-wide mt-1">Avg Pass Rate</div>
                        </div>
                        <div class="rounded-lg border border-green-300 bg-green-50 p-4 text-center">
                            <div class="text-2xl font-extrabold" [style.color]="vm.getDeltaColor(vm.trendSummary().trendDirection)">
                                {{ vm.formatDelta(vm.trendSummary().trendDirection) }}
                            </div>
                            <div class="text-xs font-semibold text-surface-500 uppercase tracking-wide mt-1">Trend Direction</div>
                        </div>
                        <div class="rounded-lg border border-red-200 bg-red-50 p-4 text-center">
                            <div class="text-2xl font-extrabold text-red-700">{{ vm.trendSummary().regressions }}</div>
                            <div class="text-xs font-semibold text-surface-500 uppercase tracking-wide mt-1">Regressions</div>
                        </div>
                    </div>
                }

                <!-- KPI Cards: Failure Analysis -->
                @if (vm.selectedReportType() === 'failure-analysis') {
                    <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
                        <div class="rounded-lg border border-red-200 bg-red-50 p-4 text-center">
                            <div class="text-2xl font-extrabold text-red-700">{{ vm.failureAnalysisSummary().totalFailed }}</div>
                            <div class="text-xs font-semibold text-surface-500 uppercase tracking-wide mt-1">Total Failed</div>
                        </div>
                        <div class="rounded-lg border border-amber-200 bg-amber-50 p-4 text-center">
                            <div class="text-2xl font-extrabold text-amber-700">{{ vm.failureAnalysisSummary().uniqueFailures }}</div>
                            <div class="text-xs font-semibold text-surface-500 uppercase tracking-wide mt-1">Unique Failures</div>
                        </div>
                        <div class="rounded-lg border border-red-300 bg-red-50 p-4 text-center">
                            <div class="text-2xl font-extrabold text-red-800">{{ vm.failureAnalysisSummary().recurring }}</div>
                            <div class="text-xs font-semibold text-surface-500 uppercase tracking-wide mt-1">Recurring (3+ runs)</div>
                        </div>
                        <div class="rounded-lg border border-surface-200 bg-blue-50 p-4 text-center">
                            <div class="text-lg font-extrabold text-blue-700">{{ vm.failureAnalysisSummary().mostImpactedArea }}</div>
                            <div class="text-xs font-semibold text-surface-500 uppercase tracking-wide mt-1">Most Impacted Area</div>
                        </div>
                        <div class="rounded-lg border border-red-200 bg-red-50 p-4 text-center">
                            <div class="text-2xl font-extrabold text-red-700">{{ vm.failureAnalysisSummary().avgFailureRate }}%</div>
                            <div class="text-xs font-semibold text-surface-500 uppercase tracking-wide mt-1">Avg Failure Rate</div>
                        </div>
                    </div>
                }

                <!-- Pass Rate Progress Bar -->
                <div class="mb-6">
                    <div class="flex items-center justify-between mb-1">
                        <span class="text-xs font-semibold text-surface-600">Overall Pass Rate</span>
                        <span class="text-xs font-bold" [style.color]="vm.getPassRateColor(vm.previewSummary().passRate)">{{ vm.previewSummary().passRate }}%</span>
                    </div>
                    <p-progressbar [value]="vm.previewSummary().passRate" [showValue]="false" styleClass="h-2" />
                </div>

                <!-- Report Type Badge -->
                @if (vm.selectedReportType() === 'filtered') {
                    <div class="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg bg-indigo-50 border border-indigo-200">
                        <i class="pi pi-sliders-h text-indigo-600"></i>
                        <span class="text-sm font-semibold text-indigo-700">Custom Report</span>
                        <span class="text-xs text-indigo-500 ml-2">Filtered by your selected criteria</span>
                    </div>
                }
                @if (vm.selectedReportType() === 'trend') {
                    <div class="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200">
                        <i class="pi pi-chart-line text-emerald-600"></i>
                        <span class="text-sm font-semibold text-emerald-700">Trend Report</span>
                        <span class="text-xs text-emerald-500 ml-2">Pass rate trends and regression tracking</span>
                    </div>
                }
                @if (vm.selectedReportType() === 'failure-analysis') {
                    <div class="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200">
                        <i class="pi pi-exclamation-triangle text-amber-600"></i>
                        <span class="text-sm font-semibold text-amber-700">Failure Analysis Report</span>
                        <span class="text-xs text-amber-500 ml-2">Recurring failures and impacted areas</span>
                    </div>
                }
                @if (vm.selectedReportType() === 'execution-summary') {
                    <div class="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg bg-sky-50 border border-sky-200">
                        <i class="pi pi-chart-bar text-sky-600"></i>
                        <span class="text-sm font-semibold text-sky-700">Execution Summary</span>
                        <span class="text-xs text-sky-500 ml-2">Single execution run overview</span>
                    </div>
                }


                <!-- Comparison Section -->
                @if (vm.isComparisonMode()) {
                    <div class="mb-6">
                        <h4 class="text-sm font-bold text-surface-700 mb-3">
                            <i class="pi pi-arrows-h mr-2 text-primary"></i>Execution Comparison
                        </h4>

                        <!-- Section 1: Runs Compared Grid -->
                        <div class="grid grid-cols-[1fr_60px_1fr] gap-0 mb-6">
                            <!-- Baseline Column -->
                            <div class="rounded-l-xl border border-red-200 bg-red-50 p-5">
                                <div class="text-xs font-bold text-red-500 uppercase tracking-wider mb-2">Baseline (Before)</div>
                                <div class="text-base font-bold text-surface-800 mb-1">{{ vm.getBaselineRunLabel() }}</div>
                                <div class="grid grid-cols-2 gap-3 mt-4">
                                    <div>
                                        <div class="text-[11px] text-surface-400">Scenarios</div>
                                        <div class="text-base font-bold">{{ vm.baselineScenarios().length }}</div>
                                    </div>
                                    <div>
                                        <div class="text-[11px] text-surface-400">Pass Rate</div>
                                        <div class="text-base font-bold" [class.text-red-600]="vm.comparisonSummary().beforePassRate < 70" [class.text-green-600]="vm.comparisonSummary().beforePassRate >= 70">{{ vm.comparisonSummary().beforePassRate }}%</div>
                                    </div>
                                    <div>
                                        <div class="text-[11px] text-surface-400">Passed</div>
                                        <div class="text-base font-bold text-green-600">{{ vm.getBaselinePassedCount() }}</div>
                                    </div>
                                    <div>
                                        <div class="text-[11px] text-surface-400">Failed</div>
                                        <div class="text-base font-bold text-red-600">{{ vm.getBaselineFailedCount() }}</div>
                                    </div>
                                </div>
                            </div>
                            <!-- VS Divider -->
                            <div class="flex items-center justify-center border-y border-surface-200 bg-primary-50 font-extrabold text-primary text-lg">VS</div>
                            <!-- Current Column -->
                            <div class="rounded-r-xl border border-green-200 bg-green-50 p-5">
                                <div class="text-xs font-bold text-green-600 uppercase tracking-wider mb-2">Current (After)</div>
                                <div class="text-base font-bold text-surface-800 mb-1">{{ vm.getComparisonRunLabel() }}</div>
                                <div class="grid grid-cols-2 gap-3 mt-4">
                                    <div>
                                        <div class="text-[11px] text-surface-400">Scenarios</div>
                                        <div class="text-base font-bold">{{ vm.currentScenarios().length }}</div>
                                    </div>
                                    <div>
                                        <div class="text-[11px] text-surface-400">Pass Rate</div>
                                        <div class="text-base font-bold" [class.text-red-600]="vm.comparisonSummary().afterPassRate < 70" [class.text-green-600]="vm.comparisonSummary().afterPassRate >= 70">{{ vm.comparisonSummary().afterPassRate }}%</div>
                                    </div>
                                    <div>
                                        <div class="text-[11px] text-surface-400">Passed</div>
                                        <div class="text-base font-bold text-green-600">{{ vm.getCurrentPassedCount() }}</div>
                                    </div>
                                    <div>
                                        <div class="text-[11px] text-surface-400">Failed</div>
                                        <div class="text-base font-bold text-red-600">{{ vm.getCurrentFailedCount() }}</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Section 2: Change Summary KPIs -->
                        <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                            <div class="rounded-xl border border-green-200 bg-green-50 p-4 text-center">
                                <div class="text-2xl font-extrabold text-green-700">+{{ vm.comparisonSummary().nowPassing }}</div>
                                <div class="text-xs font-semibold text-surface-500 uppercase tracking-wide mt-1">Now Passing</div>
                            </div>
                            <div class="rounded-xl border border-red-200 bg-red-50 p-4 text-center">
                                <div class="text-2xl font-extrabold text-red-700">{{ vm.comparisonSummary().newFailures }}</div>
                                <div class="text-xs font-semibold text-surface-500 uppercase tracking-wide mt-1">New Failures</div>
                            </div>
                            <div class="rounded-xl border border-yellow-200 bg-yellow-50 p-4 text-center">
                                <div class="text-2xl font-extrabold text-yellow-700">{{ vm.comparisonSummary().regressions }}</div>
                                <div class="text-xs font-semibold text-surface-500 uppercase tracking-wide mt-1">Regressions</div>
                            </div>
                            <div class="rounded-xl border border-purple-200 bg-purple-50 p-4 text-center">
                                @if (vm.comparisonSummary().passRateDelta >= 0) {
                                    <div class="text-2xl font-extrabold text-purple-700">+{{ vm.comparisonSummary().passRateDelta }}%</div>
                                } @else {
                                    <div class="text-2xl font-extrabold text-red-700">{{ vm.comparisonSummary().passRateDelta }}%</div>
                                }
                                <div class="text-xs font-semibold text-surface-500 uppercase tracking-wide mt-1">Pass Rate Change</div>
                            </div>
                        </div>

                        <!-- Section 3: Scenario-Level Changes -->
                        <h4 class="text-sm font-bold text-surface-700 mb-2">Scenario-Level Changes</h4>
                        <p-table [value]="vm.comparisonDeltas()" [paginator]="true" [rows]="10" styleClass="p-datatable-sm p-datatable-striped">
                            <ng-template #header>
                                <tr>
                                    <th>Change</th>
                                    <th>Scenario</th>
                                    <th>Functional Area</th>
                                    <th>Before</th>
                                    <th>After</th>
                                    <th>Details</th>
                                </tr>
                            </ng-template>
                            <ng-template #body let-delta let-i="rowIndex">
                                <tr [ngStyle]="{'background-color': delta.changeType === 'fixed' ? '#f0fdf4' : 'inherit'}">
                                    <td>
                                        <p-tag [value]="vm.getChangeTagLabel(delta.changeType)" [severity]="vm.getChangeTagSeverity(delta.changeType)" />
                                    </td>
                                    <td class="font-semibold">{{ delta.scenarioName }}</td>
                                    <td>{{ delta.functionalAreaName }}</td>
                                    <td>
                                        <p-tag [value]="delta.beforeStatus" [severity]="vm.getStatusSeverity(delta.beforeStatus)" />
                                    </td>
                                    <td>
                                        <p-tag [value]="delta.afterStatus" [severity]="vm.getStatusSeverity(delta.afterStatus)" />
                                    </td>
                                    <td class="text-xs text-surface-500">{{ delta.details }}</td>
                                </tr>
                            </ng-template>
                            <ng-template #emptymessage>
                                <tr>
                                    <td colspan="6" class="text-center text-surface-400 py-6">No scenario differences found between the two runs</td>
                                </tr>
                            </ng-template>
                        </p-table>

                        <!-- Section 4: Pass Rate by Functional Area -->
                        @if (vm.previewFunctionalAreaBreakdown().length > 0) {
                            <h4 class="text-sm font-bold text-surface-700 mt-6 mb-2">Pass Rate by Functional Area</h4>
                            <p-table [value]="vm.previewFunctionalAreaBreakdown()" styleClass="p-datatable-sm p-datatable-striped">
                                <ng-template #header>
                                    <tr>
                                        <th>Functional Area</th>
                                        <th>Scenarios</th>
                                        <th>Passed</th>
                                        <th>Failed</th>
                                        <th>Pass Rate</th>
                                        <th style="width:120px">Visual</th>
                                    </tr>
                                </ng-template>
                                <ng-template #body let-fa>
                                    <tr>
                                        <td class="font-semibold">{{ fa.name }}</td>
                                        <td>{{ fa.scenarios }}</td>
                                        <td class="text-green-600 font-semibold">{{ fa.passed }}</td>
                                        <td [class.text-red-600]="fa.failed > 0" [class.font-semibold]="fa.failed > 0">{{ fa.failed }}</td>
                                        <td>
                                            <span [class.text-green-600]="fa.passRate >= 80" [class.text-yellow-600]="fa.passRate >= 50 && fa.passRate < 80" [class.text-red-600]="fa.passRate < 50" class="font-bold">{{ fa.passRate }}%</span>
                                        </td>
                                        <td>
                                            <div class="w-full h-2 rounded-full bg-surface-200 overflow-hidden">
                                                <div class="h-full rounded-full" [style.width.%]="fa.passRate" [class.bg-green-500]="fa.passRate >= 80" [class.bg-yellow-500]="fa.passRate >= 50 && fa.passRate < 80" [class.bg-red-500]="fa.passRate < 50"></div>
                                            </div>
                                        </td>
                                    </tr>
                                </ng-template>
                            </p-table>
                        }
                    </div>
                }

                <!-- Scenario Results Table (shown for scenario and step detail levels, NOT comparison) -->
                @if (!vm.isComparisonMode() && (vm.selectedDetailLevel() === 'scenario' || vm.selectedDetailLevel() === 'step' || !vm.selectedDetailLevel())) {
                    <div class="mb-6">

                        <!-- Custom Report Table -->
                        @if (vm.selectedReportType() === 'filtered' || vm.selectedReportType() === 'execution-summary') {
                            <h4 class="text-sm font-bold text-surface-700 mb-3">Scenario Results</h4>
                            <p-table [value]="vm.previewScenarios()" [paginator]="true" [rows]="10" styleClass="p-datatable-sm p-datatable-striped">
                                <ng-template #header>
                                    <tr>
                                        <th style="width:3rem">#</th>
                                        <th>Functional Area</th>
                                        <th>Scenario</th>
                                        <th>Package</th>
                                        <th>Test Type</th>
                                        <th>Steps</th>
                                        <th>Passed</th>
                                        <th>Failed</th>
                                        <th>Status</th>
                                    </tr>
                                </ng-template>
                                <ng-template #body let-scenario let-i="rowIndex">
                                    <tr>
                                        <td>{{ i + 1 }}</td>
                                        <td>{{ scenario.functionalAreaName }}</td>
                                        <td class="font-semibold">{{ scenario.scenarioName }}</td>
                                        <td class="text-surface-500">{{ scenario.packageName }}</td>
                                        <td class="text-surface-500">{{ scenario.testTypeName }}</td>
                                        <td>{{ scenario.totalSteps }}</td>
                                        <td class="text-green-600 font-semibold">{{ scenario.passed }}</td>
                                        <td [class.text-red-600]="scenario.failed > 0" [class.font-semibold]="scenario.failed > 0">{{ scenario.failed }}</td>
                                        <td>
                                            <p-tag [value]="scenario.status" [severity]="vm.getStatusSeverity(scenario.status)" />
                                        </td>
                                    </tr>
                                </ng-template>
                                <ng-template #emptymessage>
                                    <tr>
                                        <td colspan="9" class="text-center text-surface-400 py-6">No scenarios match the selected filters</td>
                                    </tr>
                                </ng-template>
                            </p-table>
                        }

                        <!-- Trend Report Table -->
                        @if (vm.selectedReportType() === 'trend') {
                            <h4 class="text-sm font-bold text-surface-700 mb-3">
                                <i class="pi pi-chart-line mr-1 text-emerald-600"></i>Scenario Trends
                            </h4>
                            <p-table [value]="vm.previewScenarios()" [paginator]="true" [rows]="10" styleClass="p-datatable-sm p-datatable-striped">
                                <ng-template #header>
                                    <tr>
                                        <th style="width:3rem">#</th>
                                        <th>Scenario</th>
                                        <th>Functional Area</th>
                                        <th>Pass Rate</th>
                                        <th>Prev Pass Rate</th>
                                        <th>Delta</th>
                                        <th>Stability</th>
                                        <th>Status</th>
                                    </tr>
                                </ng-template>
                                <ng-template #body let-scenario let-i="rowIndex">
                                    <tr [class.bg-red-50]="scenario.stabilityTag === 'regressing' || scenario.stabilityTag === 'flaky'">
                                        <td>{{ i + 1 }}</td>
                                        <td class="font-semibold">{{ scenario.scenarioName }}</td>
                                        <td>{{ scenario.functionalAreaName }}</td>
                                        <td>
                                            <span class="font-bold" [style.color]="vm.getPassRateColor(scenario.passRate || 0)">{{ scenario.passRate }}%</span>
                                        </td>
                                        <td class="text-surface-500">{{ scenario.prevPassRate !== undefined ? scenario.prevPassRate + '%' : '—' }}</td>
                                        <td>
                                            <span class="font-bold" [style.color]="vm.getDeltaColor(scenario.delta)">{{ vm.formatDelta(scenario.delta) }}</span>
                                        </td>
                                        <td>
                                            <span class="font-bold" [style.color]="vm.getStabilityColor(scenario.stabilityTag)">{{ vm.getStabilityLabel(scenario.stabilityTag) }}</span>
                                        </td>
                                        <td>
                                            <p-tag [value]="scenario.status" [severity]="vm.getStatusSeverity(scenario.status)" />
                                        </td>
                                    </tr>
                                </ng-template>
                                <ng-template #emptymessage>
                                    <tr>
                                        <td colspan="8" class="text-center text-surface-400 py-6">No scenarios match the selected filters</td>
                                    </tr>
                                </ng-template>
                            </p-table>
                        }

                        <!-- Failure Analysis Table -->
                        @if (vm.selectedReportType() === 'failure-analysis') {
                            <h4 class="text-sm font-bold text-surface-700 mb-3">
                                <i class="pi pi-exclamation-triangle mr-1 text-amber-600"></i>Failure Analysis
                            </h4>
                            <p-table [value]="vm.previewFailedScenarios()" [paginator]="true" [rows]="10" styleClass="p-datatable-sm p-datatable-striped">
                                <ng-template #header>
                                    <tr>
                                        <th style="width:3rem">#</th>
                                        <th>Scenario</th>
                                        <th>Functional Area</th>
                                        <th>Failed Steps</th>
                                        <th>Error Category</th>
                                        <th>Recurrence</th>
                                        <th>First Failed</th>
                                        <th>Last Failed</th>
                                    </tr>
                                </ng-template>
                                <ng-template #body let-scenario let-i="rowIndex">
                                    <tr [class.bg-red-50]="scenario.failureCount >= 3">
                                        <td>{{ i + 1 }}</td>
                                        <td class="font-semibold">{{ scenario.scenarioName }}</td>
                                        <td>{{ scenario.functionalAreaName }}</td>
                                        <td class="text-red-600 font-bold">{{ scenario.failed }} / {{ scenario.totalSteps }}</td>
                                        <td class="text-surface-600">{{ scenario.errorCategory || '—' }}</td>
                                        <td class="font-semibold">{{ scenario.failureCount || 0 }} / {{ scenario.totalRunsAnalyzed || 0 }}</td>
                                        <td class="text-surface-500 text-xs">{{ scenario.firstFailedDate ? (scenario.firstFailedDate | date:'dd-MMM-yyyy') : '—' }}</td>
                                        <td class="text-surface-500 text-xs">{{ scenario.lastFailedDate ? (scenario.lastFailedDate | date:'dd-MMM-yyyy') : '—' }}</td>
                                    </tr>
                                </ng-template>
                                <ng-template #emptymessage>
                                    <tr>
                                        <td colspan="8" class="text-center text-surface-400 py-6">No failed scenarios found</td>
                                    </tr>
                                </ng-template>
                            </p-table>
                        }

                    </div>
                }

                <!-- Functional Area Breakdown — only shown when detail level is explicitly 'functional-area' -->
                @if (vm.selectedDetailLevel() === 'functional-area' && vm.previewFunctionalAreaBreakdown().length > 0) {
                    <div class="mb-6">
                        <h4 class="text-sm font-bold text-surface-700 mb-3">Results by Functional Area</h4>
                        <p-table [value]="vm.previewFunctionalAreaBreakdown()" [paginator]="vm.selectedDetailLevel() === 'functional-area'" [rows]="10" styleClass="p-datatable-sm p-datatable-striped">
                            <ng-template #header>
                                <tr>
                                    <th>Functional Area</th>
                                    <th>Scenarios</th>
                                    <th>Steps</th>
                                    <th>Passed</th>
                                    <th>Failed</th>
                                    <th>Stopped</th>
                                    <th>Pass Rate</th>
                                </tr>
                            </ng-template>
                            <ng-template #body let-fa>
                                <tr>
                                    <td class="font-semibold">{{ fa.name }}</td>
                                    <td>{{ fa.scenarios }}</td>
                                    <td>{{ fa.totalSteps }}</td>
                                    <td class="text-green-600 font-semibold">{{ fa.passed }}</td>
                                    <td [class.text-red-600]="fa.failed > 0" [class.font-semibold]="fa.failed > 0">{{ fa.failed }}</td>
                                    <td class="text-surface-400">{{ fa.stopped }}</td>
                                    <td>
                                        <span class="font-bold" [style.color]="vm.getPassRateColor(fa.passRate)">{{ fa.passRate }}%</span>
                                    </td>
                                </tr>
                            </ng-template>
                        </p-table>
                    </div>
                }

                <!-- Step-level Details (shown for Step Level detail OR Include Screenshots) -->
                @if (vm.includeScreenshots() || vm.selectedDetailLevel() === 'step') {
                    @if (vm.screenshotsLoading()) {
                        <div class="flex items-center gap-2 p-4 rounded-lg border border-surface-100 bg-surface-50 mb-4">
                            <p-progressspinner [style]="{width: '20px', height: '20px'}" strokeWidth="4" />
                            <span class="text-sm text-surface-500">Loading step details...</span>
                        </div>
                    }
                    @for (scenario of vm.previewScenarios(); track scenario.scenarioId) {
                        @if (scenario.steps?.length) {
                            <div class="mb-4 rounded-lg border border-surface-100 p-4">
                                <h5 class="text-sm font-semibold text-surface-700 mb-3">
                                    <i class="pi pi-list mr-1 text-primary"></i>{{ scenario.scenarioName }}
                                    <span class="text-xs font-normal text-surface-400 ml-2">({{ scenario.steps.length }} steps)</span>
                                </h5>
                                <div class="flex flex-col gap-2">
                                    @for (step of scenario.steps; track step.stepOrder) {
                                        <div class="flex items-start gap-3 rounded-md border border-surface-100 bg-surface-50 p-3">
                                            @if (step.screenshotUrl && vm.includeScreenshots()) {
                                                <div class="flex-shrink-0 w-48">
                                                    <img [src]="step.screenshotUrl" [alt]="'Step ' + step.stepOrder" class="rounded border border-surface-200 w-full cursor-pointer hover:opacity-80 transition-opacity" (click)="openScreenshot(step.screenshotUrl!)" />
                                                </div>
                                            }
                                            <div class="flex flex-col gap-1 flex-1">
                                                <div class="flex items-center gap-2">
                                                    <span class="text-xs font-bold text-surface-500">Step {{ step.stepOrder }}</span>
                                                    <p-tag [value]="step.status" [severity]="vm.getStatusSeverity(step.status)" />
                                                </div>
                                                <span class="text-sm text-surface-700">{{ step.stepDescription }}</span>
                                                @if (step.error) {
                                                    <span class="text-xs text-red-600 font-mono mt-1">{{ step.error }}</span>
                                                }
                                            </div>
                                        </div>
                                    }
                                </div>
                            </div>
                        }
                    }
                }

                <!-- Test Type Breakdown (shown for test-type detail level) -->
                @if (vm.selectedDetailLevel() === 'test-type' && vm.previewTestTypeBreakdown().length > 0) {
                    <div class="mb-6">
                        <h4 class="text-sm font-bold text-surface-700 mb-3">Results by Test Type</h4>
                        <p-table [value]="vm.previewTestTypeBreakdown()" [paginator]="true" [rows]="10" styleClass="p-datatable-sm p-datatable-striped">
                            <ng-template #header>
                                <tr>
                                    <th>Test Type</th>
                                    <th>Scenarios</th>
                                    <th>Steps</th>
                                    <th>Passed</th>
                                    <th>Failed</th>
                                    <th>Stopped</th>
                                    <th>Pass Rate</th>
                                </tr>
                            </ng-template>
                            <ng-template #body let-tt>
                                <tr>
                                    <td class="font-semibold">{{ tt.name }}</td>
                                    <td>{{ tt.scenarios }}</td>
                                    <td>{{ tt.totalSteps }}</td>
                                    <td class="text-green-600 font-semibold">{{ tt.passed }}</td>
                                    <td [class.text-red-600]="tt.failed > 0" [class.font-semibold]="tt.failed > 0">{{ tt.failed }}</td>
                                    <td class="text-surface-400">{{ tt.stopped }}</td>
                                    <td>
                                        <span class="font-bold" [style.color]="vm.getPassRateColor(tt.passRate)">{{ tt.passRate }}%</span>
                                    </td>
                                </tr>
                            </ng-template>
                        </p-table>
                    </div>
                }
            </div>
        }

    } <!-- end generate tab -->

    <!-- ==================== REPORT LIST TAB ==================== -->
    @if (vm.activeTab() === 'list') {

        <!-- Viewing a saved report -->
        @if (vm.viewingReport()) {
            <div class="bg-surface-0 rounded-xl border border-surface-200 p-6">
                <div class="flex items-center justify-between mb-5">
                    <div class="flex items-center gap-3">
                        <p-button icon="pi pi-arrow-left" [rounded]="true" [text]="true" severity="secondary" (onClick)="vm.closeViewReport()" />
                        <div>
                            <h3 class="text-lg font-semibold text-surface-800">{{ vm.viewingReport()!.reportName }}</h3>
                            <span class="text-xs text-surface-400">{{ vm.getGeneratedByName(vm.viewingReport()!) }} &middot; {{ vm.viewingReport()!.generatedOn | date:'medium' }}</span>
                        </div>
                    </div>
                    <div class="flex items-center gap-2">
                        <p-button icon="pi pi-file-pdf" pTooltip="Export PDF" [rounded]="true" severity="danger" (onClick)="vm.exportSavedReport(vm.viewingReport()!, 'pdf')" />
                        <p-button icon="pi pi-file-excel" pTooltip="Export Excel" [rounded]="true" severity="success" (onClick)="vm.exportSavedReport(vm.viewingReport()!, 'excel')" />
                        <p-button icon="pi pi-code" pTooltip="Export HTML" [rounded]="true" severity="info" (onClick)="vm.exportSavedReport(vm.viewingReport()!, 'html')" />
                        <p-button icon="pi pi-share-alt" pTooltip="Share" [rounded]="true" severity="warn" (onClick)="vm.shareSavedReport(vm.viewingReport()!)" />
                    </div>
                </div>

                <!-- Report Configuration Details -->
                <div class="mb-6 rounded-lg border border-surface-200 bg-surface-50 p-4">
                    <h4 class="text-xs font-bold text-surface-500 uppercase tracking-wide mb-3">
                        <i class="pi pi-cog mr-1"></i>Report Configuration
                    </h4>
                    <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        @for (detail of vm.getReportConfigDetails(vm.viewingReport()!); track detail.label) {
                            <div class="flex flex-col gap-0.5">
                                <span class="text-xs font-semibold text-surface-400 uppercase">{{ detail.label }}</span>
                                <span class="text-sm font-medium text-surface-800">{{ detail.value }}</span>
                            </div>
                        }
                    </div>
                </div>

                <!-- KPI Cards — Type-Specific -->
                @if (vm.viewingReport()?.reportType === 'trend') {
                    <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
                        <div class="rounded-lg border border-surface-200 bg-blue-50 p-4 text-center">
                            <div class="text-2xl font-extrabold text-blue-700">{{ vm.trendSummary().runsAnalyzed }}</div>
                            <div class="text-xs font-semibold text-surface-500 uppercase tracking-wide mt-1">Runs Analyzed</div>
                        </div>
                        <div class="rounded-lg border border-green-200 bg-green-50 p-4 text-center">
                            <div class="text-2xl font-extrabold text-green-700">{{ vm.trendSummary().avgPassRate }}%</div>
                            <div class="text-xs font-semibold text-surface-500 uppercase tracking-wide mt-1">Avg Pass Rate</div>
                        </div>
                        <div class="rounded-lg border border-green-300 bg-green-50 p-4 text-center">
                            <div class="text-2xl font-extrabold" [style.color]="vm.getDeltaColor(vm.trendSummary().trendDirection)">
                                {{ vm.formatDelta(vm.trendSummary().trendDirection) }}
                            </div>
                            <div class="text-xs font-semibold text-surface-500 uppercase tracking-wide mt-1">Trend Direction</div>
                        </div>
                        <div class="rounded-lg border border-red-200 bg-red-50 p-4 text-center">
                            <div class="text-2xl font-extrabold text-red-700">{{ vm.trendSummary().regressions }}</div>
                            <div class="text-xs font-semibold text-surface-500 uppercase tracking-wide mt-1">Regressions</div>
                        </div>
                    </div>
                } @else if (vm.viewingReport()?.reportType === 'failure-analysis') {
                    <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
                        <div class="rounded-lg border border-red-200 bg-red-50 p-4 text-center">
                            <div class="text-2xl font-extrabold text-red-700">{{ vm.failureAnalysisSummary().totalFailed }}</div>
                            <div class="text-xs font-semibold text-surface-500 uppercase tracking-wide mt-1">Total Failed</div>
                        </div>
                        <div class="rounded-lg border border-amber-200 bg-amber-50 p-4 text-center">
                            <div class="text-2xl font-extrabold text-amber-700">{{ vm.failureAnalysisSummary().uniqueFailures }}</div>
                            <div class="text-xs font-semibold text-surface-500 uppercase tracking-wide mt-1">Unique Failures</div>
                        </div>
                        <div class="rounded-lg border border-red-300 bg-red-50 p-4 text-center">
                            <div class="text-2xl font-extrabold text-red-800">{{ vm.failureAnalysisSummary().recurring }}</div>
                            <div class="text-xs font-semibold text-surface-500 uppercase tracking-wide mt-1">Recurring (3+ runs)</div>
                        </div>
                        <div class="rounded-lg border border-surface-200 bg-blue-50 p-4 text-center">
                            <div class="text-lg font-extrabold text-blue-700">{{ vm.failureAnalysisSummary().mostImpactedArea }}</div>
                            <div class="text-xs font-semibold text-surface-500 uppercase tracking-wide mt-1">Most Impacted Area</div>
                        </div>
                        <div class="rounded-lg border border-red-200 bg-red-50 p-4 text-center">
                            <div class="text-2xl font-extrabold text-red-700">{{ vm.failureAnalysisSummary().avgFailureRate }}%</div>
                            <div class="text-xs font-semibold text-surface-500 uppercase tracking-wide mt-1">Avg Failure Rate</div>
                        </div>
                    </div>
                } @else {
                    <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
                        <div class="rounded-lg border border-surface-200 bg-blue-50 p-4 text-center">
                            <div class="text-2xl font-extrabold text-blue-700">{{ vm.previewSummary().totalScenarios }}</div>
                            <div class="text-xs font-semibold text-surface-500 uppercase tracking-wide mt-1">Total Scenarios</div>
                        </div>
                        <div class="rounded-lg border border-green-200 bg-green-50 p-4 text-center">
                            <div class="text-2xl font-extrabold text-green-700">{{ vm.previewSummary().totalPassed }}</div>
                            <div class="text-xs font-semibold text-surface-500 uppercase tracking-wide mt-1">Passed</div>
                        </div>
                        <div class="rounded-lg border border-red-200 bg-red-50 p-4 text-center">
                            <div class="text-2xl font-extrabold text-red-700">{{ vm.previewSummary().totalFailed }}</div>
                            <div class="text-xs font-semibold text-surface-500 uppercase tracking-wide mt-1">Failed</div>
                        </div>
                        <div class="rounded-lg border border-surface-200 bg-surface-50 p-4 text-center">
                            <div class="text-2xl font-extrabold text-surface-600">{{ vm.previewSummary().totalStopped }}</div>
                            <div class="text-xs font-semibold text-surface-500 uppercase tracking-wide mt-1">Stopped</div>
                        </div>
                        <div class="rounded-lg border border-green-300 bg-green-50 p-4 text-center">
                            <div class="text-2xl font-extrabold" [style.color]="vm.getPassRateColor(vm.previewSummary().passRate)">{{ vm.previewSummary().passRate }}%</div>
                            <div class="text-xs font-semibold text-surface-500 uppercase tracking-wide mt-1">Pass Rate</div>
                        </div>
                    </div>
                }

                <!-- Pass Rate Progress Bar -->
                <div class="mb-6">
                    <div class="flex items-center justify-between mb-1">
                        <span class="text-xs font-semibold text-surface-600">Overall Pass Rate</span>
                        <span class="text-xs font-bold" [style.color]="vm.getPassRateColor(vm.previewSummary().passRate)">{{ vm.previewSummary().passRate }}%</span>
                    </div>
                    <p-progressbar [value]="vm.previewSummary().passRate" [showValue]="false" styleClass="h-2" />
                </div>

                <!-- Report Type Badge -->
                @if (vm.viewingReport()?.reportType === 'trend') {
                    <div class="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200">
                        <i class="pi pi-chart-line text-emerald-600"></i>
                        <span class="text-sm font-semibold text-emerald-700">Trend Report</span>
                        <span class="text-xs text-emerald-500 ml-2">Pass rate trends and regression tracking</span>
                    </div>
                }
                @if (vm.viewingReport()?.reportType === 'failure-analysis') {
                    <div class="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200">
                        <i class="pi pi-exclamation-triangle text-amber-600"></i>
                        <span class="text-sm font-semibold text-amber-700">Failure Analysis Report</span>
                        <span class="text-xs text-amber-500 ml-2">Recurring failures and impacted areas</span>
                    </div>
                }
                @if (vm.viewingReport()?.reportType === 'execution-summary') {
                    <div class="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg bg-sky-50 border border-sky-200">
                        <i class="pi pi-chart-bar text-sky-600"></i>
                        <span class="text-sm font-semibold text-sky-700">Execution Summary</span>
                        <span class="text-xs text-sky-500 ml-2">Single execution run overview</span>
                    </div>
                }
                @if (vm.viewingReport()?.reportType === 'filtered') {
                    <div class="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg bg-indigo-50 border border-indigo-200">
                        <i class="pi pi-sliders-h text-indigo-600"></i>
                        <span class="text-sm font-semibold text-indigo-700">Custom Report</span>
                        <span class="text-xs text-indigo-500 ml-2">Filtered by your selected criteria</span>
                    </div>
                }
                @if (vm.viewingReport()?.reportType === 'comparison') {
                    <div class="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg bg-purple-50 border border-purple-200">
                        <i class="pi pi-arrows-h text-purple-600"></i>
                        <span class="text-sm font-semibold text-purple-700">Comparison Report</span>
                        <span class="text-xs text-purple-500 ml-2">Side-by-side execution comparison</span>
                    </div>
                }

                <!-- Comparison Section (View Report) -->
                @if (vm.viewingReport()?.reportType === 'comparison') {
                    <div class="mb-6">
                        <h4 class="text-sm font-bold text-surface-700 mb-3">
                            <i class="pi pi-arrows-h mr-2 text-primary"></i>Execution Comparison
                        </h4>

                        <!-- Section 1: Runs Compared Grid -->
                        <div class="grid grid-cols-[1fr_60px_1fr] gap-0 mb-6">
                            <!-- Baseline Column -->
                            <div class="rounded-l-xl border border-red-200 bg-red-50 p-5">
                                <div class="text-xs font-bold text-red-500 uppercase tracking-wider mb-2">Baseline (Before)</div>
                                <div class="text-base font-bold text-surface-800 mb-1">{{ vm.getBaselineRunLabel() }}</div>
                                <div class="grid grid-cols-2 gap-3 mt-4">
                                    <div>
                                        <div class="text-[11px] text-surface-400">Scenarios</div>
                                        <div class="text-base font-bold">{{ vm.baselineScenarios().length }}</div>
                                    </div>
                                    <div>
                                        <div class="text-[11px] text-surface-400">Pass Rate</div>
                                        <div class="text-base font-bold" [class.text-red-600]="vm.comparisonSummary().beforePassRate < 70" [class.text-green-600]="vm.comparisonSummary().beforePassRate >= 70">{{ vm.comparisonSummary().beforePassRate }}%</div>
                                    </div>
                                    <div>
                                        <div class="text-[11px] text-surface-400">Passed</div>
                                        <div class="text-base font-bold text-green-600">{{ vm.getBaselinePassedCount() }}</div>
                                    </div>
                                    <div>
                                        <div class="text-[11px] text-surface-400">Failed</div>
                                        <div class="text-base font-bold text-red-600">{{ vm.getBaselineFailedCount() }}</div>
                                    </div>
                                </div>
                            </div>
                            <!-- VS Divider -->
                            <div class="flex items-center justify-center border-y border-surface-200 bg-primary-50 font-extrabold text-primary text-lg">VS</div>
                            <!-- Current Column -->
                            <div class="rounded-r-xl border border-green-200 bg-green-50 p-5">
                                <div class="text-xs font-bold text-green-600 uppercase tracking-wider mb-2">Current (After)</div>
                                <div class="text-base font-bold text-surface-800 mb-1">{{ vm.getComparisonRunLabel() }}</div>
                                <div class="grid grid-cols-2 gap-3 mt-4">
                                    <div>
                                        <div class="text-[11px] text-surface-400">Scenarios</div>
                                        <div class="text-base font-bold">{{ vm.currentScenarios().length }}</div>
                                    </div>
                                    <div>
                                        <div class="text-[11px] text-surface-400">Pass Rate</div>
                                        <div class="text-base font-bold" [class.text-red-600]="vm.comparisonSummary().afterPassRate < 70" [class.text-green-600]="vm.comparisonSummary().afterPassRate >= 70">{{ vm.comparisonSummary().afterPassRate }}%</div>
                                    </div>
                                    <div>
                                        <div class="text-[11px] text-surface-400">Passed</div>
                                        <div class="text-base font-bold text-green-600">{{ vm.getCurrentPassedCount() }}</div>
                                    </div>
                                    <div>
                                        <div class="text-[11px] text-surface-400">Failed</div>
                                        <div class="text-base font-bold text-red-600">{{ vm.getCurrentFailedCount() }}</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Section 2: Change Summary KPIs -->
                        <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                            <div class="rounded-xl border border-green-200 bg-green-50 p-4 text-center">
                                <div class="text-2xl font-extrabold text-green-700">+{{ vm.comparisonSummary().nowPassing }}</div>
                                <div class="text-xs font-semibold text-surface-500 uppercase tracking-wide mt-1">Now Passing</div>
                            </div>
                            <div class="rounded-xl border border-red-200 bg-red-50 p-4 text-center">
                                <div class="text-2xl font-extrabold text-red-700">{{ vm.comparisonSummary().newFailures }}</div>
                                <div class="text-xs font-semibold text-surface-500 uppercase tracking-wide mt-1">New Failures</div>
                            </div>
                            <div class="rounded-xl border border-yellow-200 bg-yellow-50 p-4 text-center">
                                <div class="text-2xl font-extrabold text-yellow-700">{{ vm.comparisonSummary().regressions }}</div>
                                <div class="text-xs font-semibold text-surface-500 uppercase tracking-wide mt-1">Regressions</div>
                            </div>
                            <div class="rounded-xl border border-purple-200 bg-purple-50 p-4 text-center">
                                @if (vm.comparisonSummary().passRateDelta >= 0) {
                                    <div class="text-2xl font-extrabold text-purple-700">+{{ vm.comparisonSummary().passRateDelta }}%</div>
                                } @else {
                                    <div class="text-2xl font-extrabold text-red-700">{{ vm.comparisonSummary().passRateDelta }}%</div>
                                }
                                <div class="text-xs font-semibold text-surface-500 uppercase tracking-wide mt-1">Pass Rate Change</div>
                            </div>
                        </div>

                        <!-- Section 3: Scenario-Level Changes -->
                        <h4 class="text-sm font-bold text-surface-700 mb-2">Scenario-Level Changes</h4>
                        <p-table [value]="vm.comparisonDeltas()" [paginator]="true" [rows]="10" styleClass="p-datatable-sm p-datatable-striped">
                            <ng-template #header>
                                <tr>
                                    <th>Change</th>
                                    <th>Scenario</th>
                                    <th>Functional Area</th>
                                    <th>Before</th>
                                    <th>After</th>
                                    <th>Details</th>
                                </tr>
                            </ng-template>
                            <ng-template #body let-delta let-i="rowIndex">
                                <tr [ngStyle]="{'background-color': delta.changeType === 'fixed' ? '#f0fdf4' : 'inherit'}">
                                    <td>
                                        <p-tag [value]="vm.getChangeTagLabel(delta.changeType)" [severity]="vm.getChangeTagSeverity(delta.changeType)" />
                                    </td>
                                    <td class="font-semibold">{{ delta.scenarioName }}</td>
                                    <td>{{ delta.functionalAreaName }}</td>
                                    <td>
                                        <p-tag [value]="delta.beforeStatus" [severity]="vm.getStatusSeverity(delta.beforeStatus)" />
                                    </td>
                                    <td>
                                        <p-tag [value]="delta.afterStatus" [severity]="vm.getStatusSeverity(delta.afterStatus)" />
                                    </td>
                                    <td class="text-xs text-surface-500">{{ delta.details }}</td>
                                </tr>
                            </ng-template>
                            <ng-template #emptymessage>
                                <tr>
                                    <td colspan="6" class="text-center text-surface-400 py-6">No scenario differences found between the two runs</td>
                                </tr>
                            </ng-template>
                        </p-table>

                        <!-- Section 4: Pass Rate by Functional Area -->
                        @if (vm.previewFunctionalAreaBreakdown().length > 0) {
                            <h4 class="text-sm font-bold text-surface-700 mt-6 mb-2">Pass Rate by Functional Area</h4>
                            <p-table [value]="vm.previewFunctionalAreaBreakdown()" styleClass="p-datatable-sm p-datatable-striped">
                                <ng-template #header>
                                    <tr>
                                        <th>Functional Area</th>
                                        <th>Scenarios</th>
                                        <th>Passed</th>
                                        <th>Failed</th>
                                        <th>Pass Rate</th>
                                        <th style="width:120px">Visual</th>
                                    </tr>
                                </ng-template>
                                <ng-template #body let-fa>
                                    <tr>
                                        <td class="font-semibold">{{ fa.name }}</td>
                                        <td>{{ fa.scenarios }}</td>
                                        <td class="text-green-600 font-semibold">{{ fa.passed }}</td>
                                        <td [class.text-red-600]="fa.failed > 0" [class.font-semibold]="fa.failed > 0">{{ fa.failed }}</td>
                                        <td>
                                            <span [class.text-green-600]="fa.passRate >= 80" [class.text-yellow-600]="fa.passRate >= 50 && fa.passRate < 80" [class.text-red-600]="fa.passRate < 50" class="font-bold">{{ fa.passRate }}%</span>
                                        </td>
                                        <td>
                                            <div class="w-full h-2 rounded-full bg-surface-200 overflow-hidden">
                                                <div class="h-full rounded-full" [style.width.%]="fa.passRate" [class.bg-green-500]="fa.passRate >= 80" [class.bg-yellow-500]="fa.passRate >= 50 && fa.passRate < 80" [class.bg-red-500]="fa.passRate < 50"></div>
                                            </div>
                                        </td>
                                    </tr>
                                </ng-template>
                            </p-table>
                        }
                    </div>
                }

                <!-- Scenario Results (non-comparison, scenario/step level only) -->
                @if (vm.viewingReport()?.reportType !== 'comparison' && (!vm.viewingReport()?.detailLevel || vm.viewingReport()?.detailLevel === 'scenario' || vm.viewingReport()?.detailLevel === 'step')) {
                    <div class="mb-6">

                        <!-- Trend Report Table -->
                        @if (vm.viewingReport()?.reportType === 'trend') {
                            <h4 class="text-sm font-bold text-surface-700 mb-3">
                                <i class="pi pi-chart-line mr-1 text-emerald-600"></i>Scenario Trends
                            </h4>
                            <p-table [value]="vm.previewScenarios()" [paginator]="true" [rows]="10" styleClass="p-datatable-sm p-datatable-striped">
                                <ng-template #header>
                                    <tr>
                                        <th style="width:3rem">#</th>
                                        <th>Scenario</th>
                                        <th>Functional Area</th>
                                        <th>Pass Rate</th>
                                        <th>Prev Pass Rate</th>
                                        <th>Delta</th>
                                        <th>Stability</th>
                                        <th>Status</th>
                                    </tr>
                                </ng-template>
                                <ng-template #body let-scenario let-i="rowIndex">
                                    <tr [class.bg-red-50]="scenario.stabilityTag === 'regressing' || scenario.stabilityTag === 'flaky'">
                                        <td>{{ i + 1 }}</td>
                                        <td class="font-semibold">{{ scenario.scenarioName }}</td>
                                        <td>{{ scenario.functionalAreaName }}</td>
                                        <td>
                                            <span class="font-bold" [style.color]="vm.getPassRateColor(scenario.passRate || 0)">{{ scenario.passRate }}%</span>
                                        </td>
                                        <td class="text-surface-500">{{ scenario.prevPassRate !== undefined ? scenario.prevPassRate + '%' : '—' }}</td>
                                        <td>
                                            <span class="font-bold" [style.color]="vm.getDeltaColor(scenario.delta)">{{ vm.formatDelta(scenario.delta) }}</span>
                                        </td>
                                        <td>
                                            <span class="font-bold" [style.color]="vm.getStabilityColor(scenario.stabilityTag)">{{ vm.getStabilityLabel(scenario.stabilityTag) }}</span>
                                        </td>
                                        <td>
                                            <p-tag [value]="scenario.status" [severity]="vm.getStatusSeverity(scenario.status)" />
                                        </td>
                                    </tr>
                                </ng-template>
                                <ng-template #emptymessage>
                                    <tr>
                                        <td colspan="8" class="text-center text-surface-400 py-6">No scenarios match the selected filters</td>
                                    </tr>
                                </ng-template>
                            </p-table>
                        }

                        <!-- Failure Analysis Table -->
                        @if (vm.viewingReport()?.reportType === 'failure-analysis') {
                            <h4 class="text-sm font-bold text-surface-700 mb-3">
                                <i class="pi pi-exclamation-triangle mr-1 text-amber-600"></i>Failure Analysis
                            </h4>
                            <p-table [value]="vm.previewFailedScenarios()" [paginator]="true" [rows]="10" styleClass="p-datatable-sm p-datatable-striped">
                                <ng-template #header>
                                    <tr>
                                        <th style="width:3rem">#</th>
                                        <th>Scenario</th>
                                        <th>Functional Area</th>
                                        <th>Failed Steps</th>
                                        <th>Error Category</th>
                                        <th>Recurrence</th>
                                        <th>First Failed</th>
                                        <th>Last Failed</th>
                                    </tr>
                                </ng-template>
                                <ng-template #body let-scenario let-i="rowIndex">
                                    <tr [class.bg-red-50]="scenario.failureCount >= 3">
                                        <td>{{ i + 1 }}</td>
                                        <td class="font-semibold">{{ scenario.scenarioName }}</td>
                                        <td>{{ scenario.functionalAreaName }}</td>
                                        <td class="text-red-600 font-bold">{{ scenario.failed }} / {{ scenario.totalSteps }}</td>
                                        <td class="text-surface-600">{{ scenario.errorCategory || '—' }}</td>
                                        <td class="font-semibold">{{ scenario.failureCount || 0 }} / {{ scenario.totalRunsAnalyzed || 0 }}</td>
                                        <td class="text-surface-500 text-xs">{{ scenario.firstFailedDate ? (scenario.firstFailedDate | date:'dd-MMM-yyyy') : '—' }}</td>
                                        <td class="text-surface-500 text-xs">{{ scenario.lastFailedDate ? (scenario.lastFailedDate | date:'dd-MMM-yyyy') : '—' }}</td>
                                    </tr>
                                </ng-template>
                                <ng-template #emptymessage>
                                    <tr>
                                        <td colspan="8" class="text-center text-surface-400 py-6">No scenarios match the selected filters</td>
                                    </tr>
                                </ng-template>
                            </p-table>
                        }

                        <!-- Default / Custom / Execution Summary Table -->
                        @if (vm.viewingReport()?.reportType !== 'trend' && vm.viewingReport()?.reportType !== 'failure-analysis') {
                            <h4 class="text-sm font-bold text-surface-700 mb-3">Scenario Results</h4>
                            <p-table [value]="vm.previewScenarios()" [paginator]="true" [rows]="10" styleClass="p-datatable-sm p-datatable-striped">
                                <ng-template #header>
                                    <tr>
                                        <th style="width:3rem">#</th>
                                        <th>Functional Area</th>
                                        <th>Scenario</th>
                                        <th>Package</th>
                                        <th>Test Type</th>
                                        <th>Steps</th>
                                        <th>Passed</th>
                                        <th>Failed</th>
                                        <th>Status</th>
                                    </tr>
                                </ng-template>
                                <ng-template #body let-scenario let-i="rowIndex">
                                    <tr>
                                        <td>{{ i + 1 }}</td>
                                        <td>{{ scenario.functionalAreaName }}</td>
                                        <td class="font-semibold">{{ scenario.scenarioName }}</td>
                                        <td class="text-surface-500">{{ scenario.packageName || '—' }}</td>
                                        <td class="text-surface-500">{{ scenario.testTypeName || '—' }}</td>
                                        <td>{{ scenario.totalSteps }}</td>
                                        <td class="text-green-600 font-semibold">{{ scenario.passed }}</td>
                                        <td [class.text-red-600]="scenario.failed > 0" [class.font-semibold]="scenario.failed > 0">{{ scenario.failed }}</td>
                                        <td>
                                            <p-tag [value]="scenario.status" [severity]="vm.getStatusSeverity(scenario.status)" />
                                        </td>
                                    </tr>
                                </ng-template>
                                <ng-template #emptymessage>
                                    <tr>
                                        <td colspan="9" class="text-center text-surface-400 py-6">No scenarios match the selected filters</td>
                                    </tr>
                                </ng-template>
                            </p-table>
                        }
                    </div>
                }

                <!-- Functional Area Breakdown — only shown when detail level is explicitly 'functional-area' -->
                @if (vm.previewFunctionalAreaBreakdown().length > 0 && vm.viewingReport()?.detailLevel === 'functional-area') {
                    <div class="mb-6">
                        <h4 class="text-sm font-bold text-surface-700 mb-3">Results by Functional Area</h4>
                        <p-table [value]="vm.previewFunctionalAreaBreakdown()" styleClass="p-datatable-sm p-datatable-striped">
                                <ng-template #header>
                                    <tr>
                                        <th>Functional Area</th>
                                        <th>Scenarios</th>
                                        <th>Steps</th>
                                        <th>Passed</th>
                                        <th>Failed</th>
                                        <th>Stopped</th>
                                        <th>Pass Rate</th>
                                    </tr>
                                </ng-template>
                                <ng-template #body let-fa>
                                    <tr>
                                        <td class="font-semibold">{{ fa.name }}</td>
                                        <td>{{ fa.scenarios }}</td>
                                        <td>{{ fa.totalSteps }}</td>
                                        <td class="text-green-600 font-semibold">{{ fa.passed }}</td>
                                        <td [class.text-red-600]="fa.failed > 0" [class.font-semibold]="fa.failed > 0">{{ fa.failed }}</td>
                                        <td class="text-surface-400">{{ fa.stopped }}</td>
                                        <td>
                                            <span class="font-bold" [style.color]="vm.getPassRateColor(fa.passRate)">{{ fa.passRate }}%</span>
                                        </td>
                                    </tr>
                                </ng-template>
                            </p-table>
                    </div>
                }

                <!-- Test Type Breakdown -->
                @if ((vm.selectedDetailLevel() === 'test-type' || vm.viewingReport()?.detailLevel === 'test-type') && vm.previewTestTypeBreakdown().length > 0) {
                    <div class="mb-6">
                        <h4 class="text-sm font-bold text-surface-700 mb-3">Results by Test Type</h4>
                        <p-table [value]="vm.previewTestTypeBreakdown()" [paginator]="true" [rows]="10" styleClass="p-datatable-sm p-datatable-striped">
                            <ng-template #header>
                                <tr>
                                    <th>Test Type</th>
                                    <th>Scenarios</th>
                                    <th>Steps</th>
                                    <th>Passed</th>
                                    <th>Failed</th>
                                    <th>Stopped</th>
                                    <th>Pass Rate</th>
                                </tr>
                            </ng-template>
                            <ng-template #body let-tt>
                                <tr>
                                    <td class="font-semibold">{{ tt.name }}</td>
                                    <td>{{ tt.scenarios }}</td>
                                    <td>{{ tt.totalSteps }}</td>
                                    <td class="text-green-600 font-semibold">{{ tt.passed }}</td>
                                    <td [class.text-red-600]="tt.failed > 0" [class.font-semibold]="tt.failed > 0">{{ tt.failed }}</td>
                                    <td class="text-surface-400">{{ tt.stopped }}</td>
                                    <td>
                                        <span class="font-bold" [style.color]="vm.getPassRateColor(tt.passRate)">{{ tt.passRate }}%</span>
                                    </td>
                                </tr>
                            </ng-template>
                        </p-table>
                    </div>
                }

                <!-- Step Screenshots (Saved Report View) -->
                @if (vm.viewingReport()?.config?.includeScreenshots) {
                    @for (scenario of vm.previewScenarios(); track $index) {
                        @if (scenario.steps?.length) {
                            <div class="mb-4 rounded-lg border border-surface-100 p-4 mt-4">
                                <h5 class="text-sm font-semibold text-surface-700 mb-3">
                                    <i class="pi pi-image mr-1 text-primary"></i>{{ scenario.scenarioName }}
                                </h5>
                                <div class="flex flex-col gap-3">
                                    @for (step of scenario.steps; track step.stepOrder) {
                                        @if (step.screenshotUrl) {
                                            <div class="flex items-start gap-3 rounded-md border border-surface-100 bg-surface-50 p-3">
                                                <div class="flex-shrink-0 w-48">
                                                    <img [src]="step.screenshotUrl" [alt]="'Step ' + step.stepOrder" class="rounded border border-surface-200 w-full cursor-pointer hover:opacity-80 transition-opacity" (click)="openScreenshot(step.screenshotUrl!)" />
                                                </div>
                                                <div class="flex flex-col gap-1">
                                                    <div class="flex items-center gap-2">
                                                        <span class="text-xs font-bold text-surface-500">Step {{ step.stepOrder }}</span>
                                                        <p-tag [value]="step.status" [severity]="vm.getStatusSeverity(step.status)" />
                                                    </div>
                                                    <span class="text-sm text-surface-700">{{ step.stepDescription }}</span>
                                                    @if (step.error) {
                                                        <span class="text-xs text-red-600 font-mono mt-1">{{ step.error }}</span>
                                                    }
                                                </div>
                                            </div>
                                        }
                                    }
                                </div>
                            </div>
                        }
                    }
                }
            </div>
        } @else {
            <!-- Report List Table -->
            <div class="bg-surface-0 rounded-xl border border-surface-200 p-6">
                <div class="flex items-center justify-between mb-5">
                    <h3 class="text-lg font-semibold text-surface-800">
                        <i class="pi pi-list mr-2 text-primary"></i>Saved Reports
                        <span class="text-sm font-normal text-surface-400 ml-2">({{ vm.savedReports().length }})</span>
                    </h3>
                    <div class="flex items-center gap-3">
                        <p-iconfield iconPosition="left">
                            <p-inputicon styleClass="pi pi-search" />
                            <input pInputText type="text" placeholder="Search reports..." class="w-64"
                                [ngModel]="vm.savedReportsSearchTerm()"
                                (ngModelChange)="vm.savedReportsSearchTerm.set($event)" />
                        </p-iconfield>
                        <p-button icon="pi pi-refresh" [rounded]="true" [text]="true" pTooltip="Refresh" (onClick)="vm.loadSavedReports()" />
                    </div>
                </div>

                <!-- Loading skeleton -->
                @if (vm.savedReportsLoading()) {
                    <div class="flex flex-col gap-3">
                        @for (i of [1,2,3,4,5]; track i) {
                            <p-skeleton height="3.5rem" styleClass="rounded-lg" />
                        }
                    </div>
                } @else if (vm.filteredSavedReports().length === 0) {
                    <div class="flex flex-col items-center justify-center py-16 text-surface-400">
                        <i class="pi pi-inbox text-5xl mb-4"></i>
                        @if (vm.savedReportsSearchTerm()) {
                            <p class="text-sm">No reports match "{{ vm.savedReportsSearchTerm() }}"</p>
                        } @else {
                            <p class="text-sm font-semibold">No saved reports yet</p>
                            <p class="text-xs mt-1">Generate a report and click "Save Report" to see it here</p>
                        }
                    </div>
                } @else {
                    <p-table
                        [value]="vm.filteredSavedReports()"
                        [paginator]="true"
                        [rows]="10"
                        [rowsPerPageOptions]="[5, 10, 25]"
                        [showCurrentPageReport]="true"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} reports"
                        styleClass="p-datatable-sm p-datatable-striped"
                        [sortField]="'generatedOn'"
                        [sortOrder]="-1"
                        [rowHover]="true">
                        <ng-template #header>
                            <tr>
                                <th pSortableColumn="reportName" style="width:11rem">Report Name <p-sortIcon field="reportName" /></th>
                                <th pSortableColumn="reportType" style="width:8rem">Type <p-sortIcon field="reportType" /></th>
                                <th style="width:7rem">Client</th>
                                <th style="width:14rem">Config Details</th>
                                <th pSortableColumn="generatedBy" style="width:7rem">Created By <p-sortIcon field="generatedBy" /></th>
                                <th pSortableColumn="generatedOn" style="width:11rem">Created <p-sortIcon field="generatedOn" /></th>
                                <th style="width:8rem">Actions</th>
                            </tr>
                        </ng-template>
                        <ng-template #body let-report>
                            <tr>
                                <td>
                                    <div class="flex items-center gap-2">
                                        <i [class]="vm.getReportTypeIcon(report.reportType) + ' text-sm'" [style.color]="vm.getReportTypeColor(report.reportType)"></i>
                                        <span class="font-semibold text-sm cursor-pointer hover:text-primary transition-colors" (click)="vm.viewSavedReport(report)">{{ report.reportName }}</span>
                                    </div>
                                </td>
                                <td>
                                    <p-tag [value]="vm.getReportTypeLabel(report.reportType)" [severity]="vm.getReportTypeSeverity(report.reportType)" [rounded]="true" />
                                </td>
                                <td class="text-xs text-surface-600">{{ vm.getClientNameForReport(report) }}</td>
                                <td class="text-xs text-surface-500">{{ vm.getConfigSummaryForReport(report) }}</td>
                                <td class="text-xs text-surface-600">{{ vm.getGeneratedByName(report) }}</td>
                                <td class="text-xs text-surface-600">{{ report.generatedOn | date:'MMM dd, yyyy hh:mm a' }}</td>
                                <td>
                                    <div class="flex items-center gap-4">
                                        <p-button icon="pi pi-eye" pTooltip="View" [rounded]="true" [outlined]="true" severity="secondary" size="small" (onClick)="vm.viewSavedReport(report)" />
                                        <p-button icon="pi pi-pencil" pTooltip="Edit" [rounded]="true" [outlined]="true" severity="info" size="small" (onClick)="vm.editSavedReport(report)" />
                                        <p-button icon="pi pi-trash" pTooltip="Delete" [rounded]="true" [outlined]="true" severity="danger" size="small" (onClick)="vm.deleteSavedReport(report)" />
                                    </div>
                                </td>
                            </tr>
                        </ng-template>
                    </p-table>
                }
            </div>
        }
    }

    <!-- Screenshot Preview Dialog -->
    <p-dialog header="Screenshot" [(visible)]="screenshotDialogVisible" [modal]="true" [style]="{ width: '80vw', maxWidth: '900px' }" [draggable]="false">
        <div class="flex justify-center">
            <img [src]="screenshotDialogUrl" alt="Screenshot" class="max-w-full max-h-[70vh] rounded" />
        </div>
    </p-dialog>

    <!-- ==================== DIALOGS ==================== -->

    <!-- Save/Update Report Dialog -->
    <p-dialog [header]="vm.isEditMode() ? 'Update Report' : 'Save Report'" [visible]="vm.showSaveDialog()" (visibleChange)="vm.showSaveDialog.set($event)" [modal]="true" [style]="{ width: '28rem' }" [draggable]="false">
        <div class="flex flex-col gap-4 pt-2">
            <div class="flex flex-col gap-2">
                <label class="text-sm font-semibold text-surface-700">Report Name</label>
                <input pInputText type="text" [ngModel]="vm.saveReportName()" (ngModelChange)="vm.saveReportName.set($event)" placeholder="Enter report name" class="w-full" />
            </div>
        </div>
        <ng-template #footer>
            <p-button label="Cancel" [text]="true" severity="secondary" (onClick)="vm.showSaveDialog.set(false)" />
            <p-button [label]="vm.isEditMode() ? 'Update' : 'Save'" [icon]="vm.isEditMode() ? 'pi pi-check' : 'pi pi-save'" (onClick)="vm.confirmSaveReport()" [disabled]="vm.isEditMode() && !vm.hasEditChanges()" />
        </ng-template>
    </p-dialog>

    <!-- Rename Report Dialog -->
    <p-dialog header="Rename Report" [visible]="vm.showRenameDialog()" (visibleChange)="vm.showRenameDialog.set($event)" [modal]="true" [style]="{ width: '28rem' }" [draggable]="false">
        <div class="flex flex-col gap-4 pt-2">
            <div class="flex flex-col gap-2">
                <label class="text-sm font-semibold text-surface-700">Report Name</label>
                <input pInputText type="text" [ngModel]="vm.renameReportName()" (ngModelChange)="vm.renameReportName.set($event)" placeholder="Enter new name" class="w-full" />
            </div>
        </div>
        <ng-template #footer>
            <p-button label="Cancel" [text]="true" severity="secondary" (onClick)="vm.showRenameDialog.set(false)" />
            <p-button label="Rename" icon="pi pi-pencil" (onClick)="vm.confirmRenameReport()" />
        </ng-template>
    </p-dialog>

    <!-- Share Report Dialog -->
    <p-dialog header="Share Report" [visible]="vm.showShareDialog()" (visibleChange)="vm.showShareDialog.set($event)" [modal]="true" [style]="{ width: '32rem' }" [draggable]="false">
        <div class="flex flex-col gap-4 pt-2">
            <p class="text-sm text-surface-600">Copy the link below to share this report with others.</p>
            <div class="flex items-center gap-2">
                <input pInputText type="text" [ngModel]="vm.shareLink()" readonly class="w-full font-mono text-xs" />
                <p-button icon="pi pi-copy" pTooltip="Copy" (onClick)="vm.copyShareLink()" />
            </div>
        </div>
        <ng-template #footer>
            <p-button label="Close" [text]="true" severity="secondary" (onClick)="vm.showShareDialog.set(false)" />
        </ng-template>
    </p-dialog>
</div>
